import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import { useNotifications } from '../context/NotificationsContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';
import { relativeTime } from '../lib/social';

const TABS = ['all', 'battles', 'aura', 'social'];

export default function NotificationsPage() {
  const nav = useNavigate();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refresh } = useNotifications();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/notifications', { params: { tab, limit: 60 } });
        setItems(data.items || []);
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [tab]);

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      refresh();
    } catch (_e) { /* ignore */ }
  };

  const linkFor = (n) => {
    if (!n) return '/home';
    if (n.subject_type === 'post') return `/post/${n.subject_id}`;
    return '/home';
  };

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim hover:text-white flex items-center gap-1"><ChevronLeft size={20} /></button>
        <div className="font-display font-black tracking-widest">{t('notifications.title')}</div>
        <button onClick={markAll} className="text-xs text-aura-cyan inline-flex items-center gap-1" data-testid="mark-all-read"><CheckCheck size={14} /></button>
      </header>

      <div className="mx-auto max-w-md flex gap-4 px-5 mt-4 border-b border-white/5">
        {TABS.map((k) => (
          <button
            key={k} onClick={() => setTab(k)}
            data-testid={`notif-tab-${k}`}
            className={`pb-2 text-xs font-display font-bold tracking-widest ${tab === k ? 'text-aura-cyan border-b-2 border-aura-cyan' : 'text-aura-textDim'}`}
          >
            {t(`notifications.tabs.${k}`)}
          </button>
        ))}
      </div>

      <main className="relative mx-auto max-w-md px-5 pt-4">
        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center text-sm text-aura-textDim">{t('notifications.empty')}</div>
        ) : (
          <div className="space-y-1" data-testid="notifs-list">
            {items.map((n) => (
              <Link
                key={n.id}
                to={linkFor(n)}
                data-testid={`notif-${n.id}`}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition ${n.read ? '' : 'bg-aura-cyan/10'}`}
              >
                <UserAvatar user={n.actor || {}} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white/95">
                    {n.actor && <span className="font-display font-semibold">@{n.actor.username}</span>} {t(`notifications.types.${n.type}`)}
                  </div>
                  <div className="text-[11px] text-aura-textDim">{relativeTime(n.created_at, locale)}</div>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-aura-cyan shadow-neon-cyan" />}
              </Link>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
